import { useAppContext } from '@aspendental/shared-components-web';
import { useMutation } from '@tanstack/react-query';

import { getChapterVirtualCenterId } from '@src/constants';
import { getLaserHairRemovalOnlineConsultId } from '@src/constants/services';
import { ZenotiError } from '@src/utils/error-handling';
import { CenterLocation } from 'types/location';

export type ReserveAppointmentResponseType = {
	is_confirmed: boolean;
	Error: ZenotiError | null;
	bookingId?: string;
	guestId?: string;
};

async function reserveAppointment(
	{
		returningGuest,
		selectedLocation,
		selectedTimeslot,
		treatmentName,
		selectedTreatmentId,
		liineSessionGUID,
		email,
		firstName,
		lastName,
		phoneNumber,
		receive_marketing_email,
		receive_transactional_sms,
		receive_marketing_sms,
		reasonCode,
		bookingId,
		guestId,
	}: {
		returningGuest: boolean | undefined;
		selectedLocation: CenterLocation | undefined;
		treatmentName: string | undefined;
		selectedTreatmentId: string;
		liineSessionGUID: string;
		selectedTimeslot: string | null;
		firstName: string;
		lastName: string;
		email: string;
		phoneNumber: string;
		receive_marketing_email: boolean;
		receive_transactional_sms: boolean;
		receive_marketing_sms: boolean;
		reasonCode: string;
		bookingId?: string;
		guestId?: string;
	},
	environment: string
) {
	const isVirtualAppointment = selectedTreatmentId === getLaserHairRemovalOnlineConsultId(environment);

	const response = await fetch(`${process.env.APP_PATH}/api/reserve-appointment/`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({
			isReturningGuest: returningGuest,
			centerId: isVirtualAppointment ? getChapterVirtualCenterId(environment) : selectedLocation?.id,
			selectedLocation: selectedLocation,
			treatmentName: treatmentName,
			liineSessionGUID: liineSessionGUID,
			selectedLocationTimezone: isVirtualAppointment
				? selectedLocation?.location.time_zone.standard_name
				: undefined,
			serviceId: selectedTreatmentId,
			timeslot: selectedTimeslot,
			guestInfo: {
				firstName,
				lastName,
				phoneNumber,
				email,
				receive_marketing_email,
				receive_transactional_sms,
				receive_marketing_sms,
				reasonCode,
				bookingId,
				guestId,
			},
		}),
	});

	const data = await response.json();

	if (!response.ok) {
		throw new Error(data.message);
	}

	return data as ReserveAppointmentResponseType;
}

export function useReserveAppointment() {
	const { environment } = useAppContext();

	const { data, mutate, isPending, isSuccess, error } = useMutation<
		ReserveAppointmentResponseType,
		Error,
		Parameters<typeof reserveAppointment>[0]
	>({
		mutationKey: ['ReserveAppointment'],
		mutationFn: (reservationData) => reserveAppointment(reservationData, environment),
	});

	return { data, mutate, isPending, isSuccess, error };
}
